import React, { useEffect, useState } from 'react';
import { Button, StatusPill } from '../../Common';
import { useNls } from '../../../nls/nlsProvider';
import { useWorkspace } from '../../../context/useWorkspace';
import { isRestrictedOpsSource } from '../../../../functions-common/src/ops/privacy';
import { recordOpsRestrictedView } from '../../../services/opsPrivacyService';
import type {
  OpsRecord,
  OpsRecordCorrection,
  OpsRecordState,
  OpsTemplate,
  OpsTemplateField,
} from '../../../types/ops';
import { useCanSendOutsideApproval } from '../approvals/useCanSendOutsideApproval';
import { SendForApprovalDialog } from '../approvals/SendForApprovalDialog';
import { OutsideApprovalsSection } from '../approvals/OutsideApprovalsSection';
import { RetentionLockChip } from '../evidence/RetentionLockChip';
import { CoSignBadge } from './CoSignBadge';
import { CoSignDialog } from './CoSignDialog';
import {
  RecordFieldValue,
  formatRecordValueText,
  formatTimestampDisplay,
} from './RecordFieldValue';

export interface RecordViewProps {
  record: OpsRecord;
  template?: OpsTemplate | null;
  templateFields: OpsTemplateField[];
  corrections: OpsRecordCorrection[];
  photoUrlsByPath: Record<string, string>;
  canAddCorrection: boolean;
  getTemplateName: (templateId: string) => string;
  getPlaceName: (placeId: string) => string;
  translateRecordState: (state: OpsRecordState) => string;
  onOpenCorrectionDialog: (rec: OpsRecord) => void;
  onBackToList: () => void;
  currentUid?: string;
  isReviewer?: boolean;
  currency?: string | null;
  onRefresh?: () => void;
}

export const RecordView: React.FC<RecordViewProps> = ({
  record,
  template,
  templateFields,
  corrections,
  photoUrlsByPath,
  canAddCorrection,
  getTemplateName,
  getPlaceName,
  translateRecordState,
  onOpenCorrectionDialog,
  onBackToList,
  currentUid = '',
  isReviewer = false,
  currency,
  onRefresh,
}) => {
  const { t, locale } = useNls();

  const { activeRole } = useWorkspace();
  const isAdmin = activeRole === 'OWNER' || activeRole === 'ADMIN';

  const { canSend: canSendApproval } = useCanSendOutsideApproval({
    workspaceId: record.workspaceId,
    subjectType: 'RECORD',
    subject: record,
    template,
    userRole: activeRole,
    isAdmin,
    currentUid,
  });

  const [sendApprovalOpen, setSendApprovalOpen] = useState(false);
  const [coSignMode, setCoSignMode] = useState<'sign' | 'proxy' | null>(null);

  useEffect(() => {
    if (!record?.id || !record?.workspaceId) return;
    const isRestricted = isRestrictedOpsSource('RECORD', record as unknown as Record<string, unknown>);
    if (!isRestricted) return;
    recordOpsRestrictedView({
      workspaceId: record.workspaceId,
      sourceType: 'RECORD',
      sourceId: record.id,
    }).catch((err) => {
      console.warn('Could not record ops restricted view audit', err);
    });
  }, [record]);

  const fieldMap = new Map<string, OpsTemplateField>();
  templateFields.forEach((f) => {
    fieldMap.set(f.key, f);
  });

  const getFieldLabel = (fieldKey: string): string =>
    fieldMap.get(fieldKey)?.label ?? fieldKey;

  const orderedKeys =
    templateFields.length > 0
      ? [
          ...templateFields.map((f) => f.key),
          ...Object.keys(record.values).filter((k) => !fieldMap.has(k)),
        ]
      : Object.keys(record.values);

  return (
    <>
      <div className="opsRegistersCardHeader">
        <div>
          <h3 className="opsRegistersCardTitle">
            {getTemplateName(record.templateId)}
          </h3>
          <p className="opsRegistersCardSubtitle">
            {getPlaceName(record.placeId)} ·{' '}
            {translateRecordState(record.state)}
          </p>
          <div style={{ marginTop: '0.25rem' }}>
            <RetentionLockChip source="ops_records" doc={record as unknown as Record<string, unknown>} />
          </div>
        </div>
        <div className="opsRegistersActionRow">
          {record.coSign?.state === 'AWAITING' &&
          record.coSign.signerRef.kind === 'MEMBER' &&
          record.coSign.signerRef.uid === currentUid &&
          currentUid !== record.authorUid ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setCoSignMode('sign')}
            >
              {template?.coSign?.label || t('NLSOpsRegisterCoSignActionAgree')}
            </Button>
          ) : null}
          {record.coSign?.state === 'AWAITING' &&
          (isAdmin || isReviewer) &&
          currentUid !== record.authorUid ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCoSignMode('proxy')}
            >
              {t('NLSOpsRegisterCoSignProxyDialogTitle')}
            </Button>
          ) : null}
          {canSendApproval ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setSendApprovalOpen(true)}
            >
              {t('NLSOpsOutsideApprovalsTitle')}
            </Button>
          ) : null}
          {canAddCorrection &&
          (record.state === 'SUBMITTED' ||
            record.state === 'REVIEWED' ||
            record.state === 'APPROVED') ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onOpenCorrectionDialog(record)}
            >
              {t('NLSOpsAddCorrection')}
            </Button>
          ) : null}
          <Button variant="tertiary" size="sm" onClick={onBackToList}>
            ← {t('NLSOpsTabRecords')}
          </Button>
        </div>
      </div>

      <div className="opsRegistersTagRow">
        <StatusPill
          tone={
            record.state === 'APPROVED'
              ? 'success'
              : record.state === 'RETURNED'
                ? 'danger'
                : 'info'
          }
        >
          {translateRecordState(record.state)}
        </StatusPill>
        {record.late ? (
          <span className="opsRegistersBadge opsRegistersBadgeDanger opsRegistersFigures">
            {t('NLSOpsLateBadge', {
              minutes: String(record.lateBy ?? 0),
            })}
          </span>
        ) : null}
        {record.selfApproved ? (
          <span className="opsRegistersBadge opsRegistersBadgeWarn">
            {t('NLSOpsSelfApprovedBadge')}
          </span>
        ) : null}
        {record.state === 'APPROVED' ? (
          <span className="opsRegistersBadge opsRegistersBadgeSuccess">
            {t('NLSOpsLockedBadge')}
          </span>
        ) : null}
        {(record.exceptions ?? []).map((ex, i) => (
          <span key={i} className="opsRegistersBadge opsRegistersBadgeWarn">
            {getFieldLabel(ex.fieldKey)}: {ex.reason}
          </span>
        ))}
        {record.coSign && (
          <CoSignBadge coSign={record.coSign} config={template?.coSign} />
        )}
      </div>

      {record.selfApproved && record.selfApprovalReason ? (
        <div className="opsRegistersFieldHint">
          {record.selfApprovalReason}
        </div>
      ) : null}

      {/* Timestamps */}
      <div className="opsRegistersKVGrid">
        <div className="opsRegistersKVItem">
          <span className="opsRegistersKVKey">{t('NLSOpsOccurredAt')}</span>
          <span className="opsRegistersKVVal opsRegistersFigures">
            {formatTimestampDisplay(record.occurredAt, locale)}
          </span>
        </div>
        <div className="opsRegistersKVItem">
          <span className="opsRegistersKVKey">{t('NLSOpsCapturedAt')}</span>
          <span className="opsRegistersKVVal opsRegistersFigures">
            {formatTimestampDisplay(record.capturedAt, locale)}
          </span>
        </div>
        <div className="opsRegistersKVItem">
          <span className="opsRegistersKVKey">{t('NLSOpsReceivedAt')}</span>
          <span className="opsRegistersKVVal opsRegistersFigures">
            {formatTimestampDisplay(record.receivedAt, locale)}
          </span>
        </div>
      </div>

      {/* Values & photo thumbnails formatted by field type */}
      <div className="opsRegistersKVGrid">
        {orderedKeys.map((k) => {
          const field = fieldMap.get(k);
          const v = record.values[k];
          return (
            <div key={k} className="opsRegistersKVItem">
              <span className="opsRegistersKVKey">{field?.label ?? k}</span>
              <RecordFieldValue
                field={field}
                value={v}
                photoUrlsByPath={photoUrlsByPath}
                currency={currency}
              />
            </div>
          );
        })}
      </div>

      {/* State timeline */}
      <div className="opsRegistersTimeline">
        <div className="opsRegistersTimelineStep">
          <span>{t('NLSOpsStateSubmitted')}</span>
          <span className="opsRegistersFigures">
            {formatTimestampDisplay(record.receivedAt, locale)}
          </span>
        </div>
        {record.returnedReason ? (
          <div className="opsRegistersTimelineStep">
            <span>
              {t('NLSOpsStateReturned')}: {record.returnedReason}
            </span>
            <span className="opsRegistersFigures">
              {formatTimestampDisplay(record.returnedAt, locale)}
            </span>
          </div>
        ) : null}
        {record.reviewedAt ? (
          <div className="opsRegistersTimelineStep">
            <span>{t('NLSOpsStateReviewed')}</span>
            <span className="opsRegistersFigures">
              {formatTimestampDisplay(record.reviewedAt, locale)}
            </span>
          </div>
        ) : null}
        {record.approvedAt ? (
          <div className="opsRegistersTimelineStep">
            <span>{t('NLSOpsStateApproved')}</span>
            <span className="opsRegistersFigures">
              {formatTimestampDisplay(record.approvedAt, locale)}
            </span>
          </div>
        ) : null}
      </div>

      {/* Correction trail */}
      <div className="opsRegistersFieldGroup">
        <h4 className="opsRegistersCardTitle">{t('NLSOpsCorrectionTrail')}</h4>
        {corrections.length === 0 ? (
          <p className="opsRegistersCardSubtitle">
            {t('NLSOpsNoCorrectionsYet')}
          </p>
        ) : (
          <div className="opsRegistersList">
            {corrections.map((c) => (
              <div key={c.id} className="opsRegistersBuilderFieldCard">
                <div className="opsRegistersRowTop">
                  <span className="opsRegistersRowTitle">{c.reason}</span>
                  <span className="opsRegistersRowMeta opsRegistersFigures">
                    {c.authorName || c.authorUid} ·{' '}
                    {formatTimestampDisplay(c.createdAt, locale)}
                  </span>
                </div>
                <div className="opsRegistersRowMeta opsRegistersFigures">
                  {(c.diffs ?? []).map((diff) => {
                    const diffField = fieldMap.get(diff.fieldKey);
                    const beforeText = formatRecordValueText(
                      diffField,
                      diff.before,
                      t,
                      locale,
                    );
                    const afterText = formatRecordValueText(
                      diffField,
                      diff.after,
                      t,
                      locale,
                    );
                    return (
                      <span key={diff.fieldKey}>
                        {diffField?.label ?? diff.fieldKey}: {beforeText} →{' '}
                        {afterText}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <OutsideApprovalsSection
        workspaceId={record.workspaceId}
        subjectType="RECORD"
        subjectId={record.id ?? ''}
        subjectVersion={record.templateVersion}
        canSend={canSendApproval}
        onOpenSendDialog={() => setSendApprovalOpen(true)}
        userRole={activeRole}
        isAdmin={isAdmin}
        currentUid={currentUid}
      />

      {sendApprovalOpen && (
        <SendForApprovalDialog
          open={sendApprovalOpen}
          onDismiss={() => setSendApprovalOpen(false)}
          workspaceId={record.workspaceId}
          subjectType="RECORD"
          subjectId={record.id ?? ''}
        />
      )}

      {coSignMode && (
        <CoSignDialog
          open={Boolean(coSignMode)}
          onDismiss={() => setCoSignMode(null)}
          record={record}
          mode={coSignMode}
          onSuccess={onRefresh}
        />
      )}
    </>
  );
};
